import { FunctionComponent, useEffect, useRef } from 'react'
import { useDictationContext } from '../../../core/provider/dictation-context.provider'
import { Icon } from '../../shared/Icon'
import styles from './DictationWidget.module.scss'

type Props = {
  transcript: string
  copied: boolean
  panelDirection: 'down' | 'up'
  onTranscriptChange: (value: string) => void
  onCopy: () => void
  onReset: () => void
}

export const DictationNotepad: FunctionComponent<Props> = ({
  transcript,
  copied,
  panelDirection,
  onTranscriptChange,
  onCopy,
  onReset,
}) => {
  const { registerNotepadTextarea, setNotepadCaret } = useDictationContext()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    registerNotepadTextarea(textareaRef.current)
    return () => registerNotepadTextarea(null)
  }, [registerNotepadTextarea])

  // Quand la zone n'a pas le focus, la dictée s'ajoute à la fin : on suit en bas.
  // Le cas « zone focus » (insertion au curseur) est géré dans le provider, qui
  // scrolle jusqu'au curseur — voir scrollTextareaToCaret.
  useEffect(() => {
    const el = textareaRef.current
    if (el && document.activeElement !== el) el.scrollTop = el.scrollHeight
  }, [transcript])

  return (
    <div
      className={`${styles.panel} ${panelDirection === 'up' ? styles.panelUp : styles.panelDown}`}
    >
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        value={transcript}
        onChange={(e) => onTranscriptChange(e.target.value)}
        // Suit le curseur réel de l'utilisateur (clic, flèches, sélection) pour
        // que la dictée s'insère au bon endroit même quand React vient de
        // réécrire la valeur — voir setNotepadCaret dans le provider.
        onSelect={(e) =>
          setNotepadCaret({
            start: e.currentTarget.selectionStart ?? 0,
            end: e.currentTarget.selectionEnd ?? 0,
          })
        }
        onBlur={() => setNotepadCaret(null)}
        placeholder="Parlez… le texte s'écrira ici."
      />
      <div className={styles.actionRow}>
        <button
          className={`${styles.actionBtn} ${styles.actionBtnCta} ${copied ? styles.actionBtnCopied : ''}`}
          disabled={!transcript}
          onClick={onCopy}
        >
          {copied ? (
            <Icon icon="check" size="pico" color="currentColor" />
          ) : (
            <Icon icon="copy" size="pico" />
          )}
          {copied ? 'Copié' : 'Copier'}
        </button>
        <button className={styles.actionBtn} disabled title="Bientôt disponible">
          <Icon icon="pencil" size="pico" />
          Corriger
        </button>
        <div className={styles.actionSpacer} />
        <button
          className={`${styles.actionBtn} ${styles.actionBtnIcon}`}
          disabled={!transcript}
          onClick={onReset}
          title="Effacer"
        >
          <Icon icon="trash" size="pico" />
        </button>
      </div>
    </div>
  )
}
